import { NgInject } from '@df/ng/ng-inject';
import each from 'lodash-es/each';
import filter from 'lodash-es/filter';
import forOwn from 'lodash-es/forOwn';
import mapValues from 'lodash-es/mapValues';
import reduce from 'lodash-es/reduce';
import values from 'lodash-es/values';
import { Subject, Subscription } from 'rxjs';
import {
    BundleOptionSelection,
    IBundleOptionSelectionSelection,
    IBundleSelectionOptions,
    IBundleSelectionOptionsData,
    IBundleSelectionProducts
} from '@df/catalog/product/bundle-option-selection';
import { BundleOptionSelectionFactory } from '@df/catalog/product/bundle-option-selection.factory';
import { IProductDataBundleOption, IProductDataBundleOptionChild, Product } from '@df/catalog/product/product';

/**
 * Its an object with qties for each selected option for given qty
 */

export class BundleSelection {
    protected _options: IBundleSelectionOptions = {};

    /**
     * Cached object created from _options
     * @see selection getter
     * @type {BundleSelection}
     */
    protected _selection?: IBundleSelectionOptionsData;

    /**
     * Observers list
     * @see  observ
     * @type {Array}
     */
    protected bundleSelectionSubject = new Subject<BundleSelection>();

    @NgInject(BundleOptionSelectionFactory)
    private bundleOptionSelectionFactory!: BundleOptionSelectionFactory;

    constructor(
        readonly _model: Product,
        data?: IBundleSelectionOptionsData | BundleSelection
    ) {
        if (data && data instanceof BundleSelection) {
            data = data.selection;
        }

        const bundleOptions = _model.bundleOptions || [];

        bundleOptions.forEach(bundleOption => {
            this._options[bundleOption.id] = this.createOptionSelection(bundleOption, data ? data[bundleOption.id] : undefined);
        });
    }

    protected createOptionSelection(bundleOption: IProductDataBundleOption, data?: IBundleOptionSelectionSelection) {
        return this.bundleOptionSelectionFactory.create(this, bundleOption, data);
    }

    /**
     * Data setter
     * @param {IBundleSelectionOptionsData} data [description]
     */
    setData(data: IBundleSelectionOptionsData) {
        forOwn(this._options, (value, optionId) => {
            this._options[optionId].setData(data[optionId]);
        });
    }

    /**
     * Adds observer function
     * Observer function are called on any children changes
     * @param  {(resultData) => void} callback
     */
    subscribe(observer): Subscription {
        return this.bundleSelectionSubject.subscribe(observer);
    }

    /**
     * Trigger observable change
     */
    next() {
        this.bundleSelectionSubject.next(this);
    }

    /////////////////////////////////////////////
    ///
    /// Getting information
    ///
    /////////////////////////////////////////////

    /**
     * Gets qty for given option and selection pair
     * @param  {number | IProductDataBundleOption}      option    Bundle option
     * @param  {number | IProductDataBundleOptionChild} selection Bundle option child
     * @return {number}    picked qty
     */
    getQty(option: number | IProductDataBundleOption, selection: number | IProductDataBundleOptionChild): number {
        const item = this._options[this._toOptionId(option)];
        if (!item) {
            return 0;
        }
        return item.getQty(selection);
    }

    /**
     * Checks if given option - selection pair is picked
     * @param  {number | IProductDataBundleOption}      option    Bundle option
     * @param  {number | IProductDataBundleOptionChild} selection Bundle option child
     * @return {boolean}     true if picked
     */
    isPicked(option: number | IProductDataBundleOption, selection: number | IProductDataBundleOptionChild): boolean {
        return !!this.getQty(option, selection);
    }

    /**
     * Getts selection object
     * @return {BundleSelectionOptionsData}
     */
    protected optionsToSelection(options: BundleOptionSelection[]): IBundleSelectionOptionsData {
        const selection: IBundleSelectionOptionsData = {};
        each(options, option => (selection[option.option.id] = option.selection));
        return selection;
    }

    /**
     * Retursn complete selection (all options with qties)
     * @return {IBundleSelectionOptionsData}
     */
    get selection(): IBundleSelectionOptionsData {
        if (!this._selection) {
            this._selection = this.optionsToSelection(values(this.options));
        }
        return this._selection;
    }

    /**
     * Returns selection skipping items that are not editable
     * (not editable means that its required, only one option is possible and user cant change qty)
     * @return {IBundleSelectionOptionsData}
     */
    get editableSelection(): IBundleSelectionOptionsData {
        return this.optionsToSelection(this.editableOptions);
    }

    /**
     * Retuns options object
     */
    get options() {
        return this._options;
    }

    /**
     * Retuns options that are editable (not required or more then on option to pick)
     */
    get editableOptions() {
        return filter(values(this._options), option => option.editable === true);
    }

    /**
     * Returns selected products with qties
     */
    getSelectedProducts(): IBundleSelectionProducts {
        const map: IBundleSelectionProducts = {};

        let order = 0;

        forOwn(this._options, (value, optionId) => {
            const selectionOption: BundleOptionSelection = this._options[optionId];
            const products = selectionOption.getSelectedProducts();
            if (products) {
                forOwn(products, (product, productId) => {
                    const data = products[productId];
                    if (!map[productId]) {
                        map[productId] = { ...data, order: order++ };
                    } else {
                        map[productId].qty += data.qty;
                    }
                });
            }
        });
        return map;
    }

    /**
     * Retrieves options selection hash
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {string} [description]
     */
    get selectionHash(): string {
        return JSON.stringify(mapValues(this.getSelectedProducts(), val => val.qty));
    }

    getPrice(): number {
        return this.calcPrice(false);
    }

    getOrgPrice(): number {
        return this.calcPrice(true);
    }

    calcPrice(orgPrice: boolean = false): number {
        const a = reduce(
            this._options,
            (memo: number, option: BundleOptionSelection) => {
                return memo + option.calcPrice(orgPrice);
            },
            0
        );
        return a;
    }

    /////////////////////////////////////////////
    ///
    /// Internal things
    ///
    /////////////////////////////////////////////

    /**
     * Normalize option to id (number)
     * @param  {number | IProductDataBundleOption} option
     * @return {number}      option id
     */
    protected _toOptionId(option: number | IProductDataBundleOption): number {
        return typeof option === 'number' ? option : option.id;
    }
}
